<template>
  <div>
    <template v-if="$route.name === 'ClassGroups'">
      <GroupFilter @search="handleSearch" @add="openAddGroupDialog" />

      <div class="col-12 col-xl-12">
        <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
          <div
            class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
            style="height: 60px"
          >
            <div
              class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100"
            >
              <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
                {{ $t('groups.groupsOf') }} {{ className }}
              </h6>
            </div>
          </div>

          <div class="card-body px-0 pb-2 pe-xl-3">
            <div class="table-responsive p-1 p-xl-4">
              <!-- Loading State -->
              <LoadingSpinner
                v-if="isLoading"
                :message="$t('loading.message')"
                class="py-5"
              />
              
              <!-- Mobile View -->
              <div v-if="!isLoading" class="d-block d-xl-none">
                <div v-if="filteredGroups.length === 0" class="text-center py-3 text-muted">
                  <i class="fas fa-users-slash fa-2x mb-2"></i>
                  <p>{{ $t('groups.noGroupsAvailable') }}</p>
                </div>
                
                <div v-for="group in filteredGroups" :key="'mobile-' + group.id" class="card mb-3 mx-2">
                  <div class="card-body py-2">
                    <div class="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 class="mb-0 text-sm">{{ group.group.name }}</h6>
                        <p class="text-xs text-muted mb-1">{{ $t('groups.studentsCount') }}: {{ group.students_count }}</p>
                        <p class="text-xs text-muted mb-0">{{ $t('groups.creationDate') }}: {{ formatDate(group.created_at) }}</p>
                      </div>
                      <div class="d-flex">
                        <button
                          @click="navigateToGroupSchedule(group)"
                          class="btn btn-link text-info p-0 mx-1"
                          :title="$t('groups.viewSchedule')"
                          v-can="'class_schedules.display'"
                        >
                          <i class="fas fa-calendar-alt fa-xs"></i>
                        </button>
                        <button
                          @click="navigateToGroupStudents(group)"
                          class="btn btn-link text-primary p-0 mx-1"
                          :title="$t('groups.viewStudents')"
 v-can="'class_group_students.display'"
                        >
                          <i class="fas fa-users fa-xs"></i>
                        </button>
                      </div>
                    </div>
                    
                    <div class="d-flex justify-content-end mt-2">
                      <button
                        @click.stop="openRemoveGroupDialog(group)"
                        class="btn btn-link text-danger p-0 mx-1"
                        :title="$t('groups.removeFromClass')"
                        v-can="'class_groups.delete'"
                      >
                        <i class="fas fa-trash fa-xs"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Desktop View -->
              <table v-if="!isLoading" class="table align-items-center mb-0 d-none d-xl-table">
                <thead>
                  <tr>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7">
                      {{ $t('groups.groupName') }}
                    </th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                      {{ $t('groups.studentsCount') }}
                    </th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                      {{ $t('groups.creationDate') }}
                    </th>
                    <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center ">
                      {{ $t('groups.actions') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="filteredGroups.length === 0">
                    <td colspan="4" class="text-center py-4 text-muted">
                      <i class="fas fa-users-slash fa-2x mb-2"></i>
                      <p>{{ $t('groups.noGroupsAvailable') }}</p>
                    </td>
                  </tr>
                  
                  <tr v-for="group in filteredGroups" :key="group.id">
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-md">{{ group.group.name }}</h6>
                        </div>
                      </div>
                    </td>
    
                    <td class="align-middle text-center">
                      <span class="badge bg-primary">
                        {{ group.students_count }}
                      </span>
                    </td>

                    <td class="align-middle text-center">
                      <span class="text-xs font-weight-bold">
                        {{ formatDate(group.created_at) }}
                      </span>
                    </td>
                    
                  <td class="align-middle pe-4" :class="{'text-center': !canModify, 'text-center': canModify}">
  <div class="d-inline-flex" :class="{'justify-content-center': !canModify}">
    <button
      @click="navigateToGroupSchedule(group)"
      class="btn btn-link text-info p-1 mx-1"
      :title="$t('groups.viewSchedule')"
      v-can="'class_schedules.display'"
    >
      <i class="fas fa-calendar-alt fa-sm"></i>
    </button>
    <button
      @click="navigateToGroupStudents(group)"
      class="btn btn-link text-primary p-1 mx-1"
      :title="$t('groups.viewStudents')"
      v-can="'class_group_students.display'"
    >
      <i class="fas fa-users fa-sm"></i>
    </button>
    <button
      @click.stop="openRemoveGroupDialog(group)"
      class="btn btn-link text-danger p-1 mx-1"
      :title="$t('groups.removeFromClass')"
      v-can="'class_groups.delete'"
    >
      <i class="fas fa-trash fa-sm"></i>
    </button>
  </div>
</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <RemoveGroupDialog
        ref="removeGroupDialog"
        :group-name="selectedGroup?.group?.name"
        @remove="handleRemoveGroupFromClass"
      />
    </template>
    <router-view></router-view>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { useI18n } from 'vue-i18n';
import GroupFilter from "@/components/features/groups/filters/GroupFilter.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { useToast } from "vue-toastification";
import RemoveGroupDialog from '@/components/features/groups/dialogs/RemoveGroupDialog.vue';

const route = useRoute();
const store = useStore();
const toast = useToast();
const router = useRouter();
const { t, locale } = useI18n();

const classId = route.params.classId;
const className = ref("");
const classGroups = ref([]);
const isLoading = ref(true);
const searchTerm = ref("");
const pagination = computed(() => store.state.groups.pagination);

const openAddGroupDialog = () => {
  router.push({
    name: 'AssignGroup',
    params: {
      classId: route.params.classId
    }
  });
}
const fetchClassData = async () => {
  try {
    const response = await store.dispatch("classes/fetchClass", classId);
    className.value = response.data?.name || "";
  } catch (error) {
    toast.error(t('classes.messages.loadError'));
    console.error("Error fetching class:", error);
  }
};

const fetchClassGroups = async (page = 1, perPage = null) => {
  isLoading.value = true;
  try {
    const response = await store.dispatch("groups/fetchClassGroups", {
      classId,
      page,
      perPage: perPage || pagination.value.perPage,
    });

    classGroups.value = response.data || [];

    if (response.meta) {
      store.commit("groups/SET_PAGINATION", {
        current_page: response.meta.current_page,
        last_page: response.meta.last_page,
        total: response.meta.total,
        per_page: response.meta.per_page,
        from: response.meta.from,
        to: response.meta.to,
      });
    }
  } catch (error) {
    toast.error(t('groups.loadError'));
    console.error("Error fetching class groups:", error);
    classGroups.value = [];
  } finally {
    isLoading.value = false;
  }
};

const formatDate = (dateString) => {
  const options = { year: "numeric", month: "long", day: "numeric" };
  return new Date(dateString).toLocaleDateString(locale.value, options);
};

const navigateToGroupStudents = (group) => {
  router.push({
    name: "GroupStudents",
    params: {
      classId: classId,
      groupId: group.group.id,
    },
  });
};

const navigateToGroupSchedule = (group) => {
  router.push({
    name: "GroupSchedule",
    params: {
      classId: classId,
      groupId: group.group.id,
    },
  });
};

const filteredGroups = computed(() => {
  if (!searchTerm.value) return classGroups.value;

  const term = searchTerm.value.toLowerCase();
  
  return classGroups.value.filter((group) => {
    const groupName = group?.group?.name?.toLowerCase() || '';
    const studentCount = group?.students_count?.toString() || '0';
    const createdAt = group?.created_at || '';

    return (
      groupName.includes(term) ||
      studentCount.includes(term) ||
      createdAt.includes(term)
    );
  });
});

const handleSearch = (term) => {
  searchTerm.value = term;
};

const selectedGroup = ref(null);
const removeGroupDialog = ref(null);

const openRemoveGroupDialog = (group) => {
  selectedGroup.value = group;
  if (group.students_count > 0) {
    toast.error(t('groups.cannotRemoveWithStudents'));
    return;
  }
  
  removeGroupDialog.value.openDialog();
};

const handleRemoveGroupFromClass = async () => {
  try {
    if (!selectedGroup.value) {
      toast.error(t('groups.noGroupSelected'));
      return;
    }
    
    await store.dispatch('groups/removeGroupFromClass', {
      classId: classId,
      groupId: selectedGroup.value.group.id
    });
    
    toast.success(t('groups.removeSuccess'));
    await fetchClassGroups(); 
  } catch (error) {
    console.error('Error details:', error);
    toast.error(error.response?.data?.message || t('groups.removeError'));
  }
};

onMounted(async () => {
  await Promise.all([fetchClassData(), fetchClassGroups()]);
});
</script>